import { hideDefaultCause } from "utils/causes/archieveCause";
import { useAppState } from "contexts/UserContext";
import { useAuth } from "contexts/AuthContext";
import { useCause } from "contexts/CauseContext";
import type { Dispatch, SetStateAction } from "react";
import { FeelingCause } from "../../../types/feeling-cause";
import { CauseTypeService } from "../../../services/CauseTypeService";

type CauseStateSetter = Dispatch<SetStateAction<FeelingCause[]>>;

interface UseCauseDeleteProps {
  setActiveCauseTypes?: CauseStateSetter;
  setActiveTeamCauseTypes?: CauseStateSetter;
}

export function useCauseDelete({
  setActiveCauseTypes,
  setActiveTeamCauseTypes,
}: UseCauseDeleteProps): any {
  const { clearCache } = useAppState();
  const { userId } = useAuth();
  const { setDefaultCauseTypes } = useCause();

  const removeCauseFromLocalState = (id: string) => {
    if (setActiveCauseTypes) {
      setActiveCauseTypes((prev) =>
        (prev || []).filter((causeItem: FeelingCause) => causeItem.id !== id)
      );
    }
    if (setActiveTeamCauseTypes) {
      setActiveTeamCauseTypes((prev: FeelingCause[]) =>
        (prev || []).filter((causeItem: FeelingCause) => causeItem.id !== id)
      );
    }
  };

  const handleCauseDelete = async (
    cause: FeelingCause,
    organizationId: string
  ) => {
    const id = cause?.id;
    console.log("🗑️ handleCauseDelete: Starting cause deletion/archival");
    console.log("🗑️ handleCauseDelete: Input data:", {
      id,
      organizationId,
      uid: cause?.uid,
    });

    try {
      if (cause?.uid === "*") {
        console.log("🗑️ handleCauseDelete: Hiding default cause for user...");
        await hideDefaultCause(id, userId);
        console.log("🗑️ handleCauseDelete: ✅ Default cause hidden");
        setDefaultCauseTypes((prev: FeelingCause[]) =>
          (prev || []).filter((causeItem: FeelingCause) => causeItem.id !== id)
        );
        clearCache("defaultCauseTypes");
      } else {
        console.log(
          "🗑️ handleCauseDelete: Calling archiveCauseType service function..."
        );
        await CauseTypeService.archiveCauseType(id, organizationId);
        console.log(
          "🗑️ handleCauseDelete: ✅ Document successfully archived via service"
        );
      }

      // Update local state (personal + team lists; only the list that contained the id changes)
      console.log("🗑️ handleCauseDelete: Updating local state...");
      removeCauseFromLocalState(id);

      console.log(
        "🗑️ handleCauseDelete: ✅ Successfully completed cause archival"
      );
    } catch (error: any) {
      console.error("🗑️ handleCauseDelete: ❌ Error archiving cause:", error);
      console.error("🗑️ handleCauseDelete: Error details:", {
        message: error?.message,
        code: error?.code,
        codeName: error?.codeName,
        stack: error?.stack,
      });

      if (
        error?.code === "permission-denied" ||
        error?.codeName === "permission-denied"
      ) {
        console.error(
          "🗑️ handleCauseDelete: ⚠️ PERMISSION DENIED - Check Firestore security rules for causeTypes collection"
        );
        console.error(
          "🗑️ handleCauseDelete: members may need permission to update causes"
        );
      }
    }
  };

  return { handleCauseDelete };
}
